'use client';
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface GenData {
  gen: string;
  label: string;
  execCount: number;
  gsv: number;
  bonusPct: number;
  bonus: number;
  isBreakaway: boolean;
}

const GEN_BAR_COLORS: Record<string, string> = {
  G1: '#a78bfa',
  G2: '#60a5fa',
  G3: '#34d399',
  G4: '#fbbf24',
  G5: '#fb923c',
  G6: '#f87171',
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs font-semibold text-zinc-300 mb-1">{label}</p>
      <p className="text-xs font-mono text-emerald-400 font-bold">${payload[0]?.value?.toFixed(0)}</p>
    </div>
  );
};

export default function ExecutiveDetailRows({ generationData }: { generationData: GenData[] }) {
  const total = generationData.reduce((s, g) => s + g.bonus, 0);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-zinc-300">Commission by Generation</h3>
        <span className="text-xs font-bold text-emerald-400 font-mono">${total.toFixed(0)} total</span>
      </div>

      {/* Bar Chart */}
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={generationData} margin={{ top: 4, right: 4, left: -15, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="gen" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v.toFixed(0)}`} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="bonus" radius={[4, 4, 0, 0]}>
            {generationData.map(g => (
              <Cell key={`cell-${g.gen}`} fill={GEN_BAR_COLORS[g.gen] || '#71717a'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Detail Table */}
      <div className="mt-4 space-y-2">
        {generationData.map(g => {
          const pct = total > 0 ? (g.bonus / total) * 100 : 0;
          return (
            <div key={`edr-${g.gen}`} className="flex items-center gap-3">
              <span className="text-xs font-bold font-mono w-6 flex-shrink-0" style={{ color: GEN_BAR_COLORS[g.gen] }}>{g.gen}</span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] text-zinc-500">{g.execCount} execs · {(g.gsv / 1000).toFixed(1)}K GSV · {g.bonusPct}%</span>
                  <span className="text-xs font-bold font-mono text-emerald-400">${g.bonus.toFixed(0)}</span>
                </div>
                <div className="w-full bg-zinc-800 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: GEN_BAR_COLORS[g.gen] }}
                  />
                </div>
              </div>
              <span className="text-[10px] text-zinc-600 font-mono w-8 text-right">{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>

      {/* Threshold info */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="bg-zinc-800/40 rounded-xl p-3 border border-zinc-700">
          <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold mb-1">Full 5% Rate Threshold</p>
          <p className="text-sm font-bold text-emerald-400 font-mono">GSV ≥ 3,000</p>
          <p className="text-[9px] text-zinc-600 mt-0.5">Applies to all G1–G6 breakaway execs</p>
        </div>
        <div className="bg-zinc-800/40 rounded-xl p-3 border border-zinc-700">
          <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold mb-1">Reduced 2.5% Rate</p>
          <p className="text-sm font-bold text-amber-400 font-mono">GSV 2,000–2,999</p>
          <p className="text-[9px] text-zinc-600 mt-0.5">Half rate when below 3K GSV threshold</p>
        </div>
      </div>
    </div>
  );
}